import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin } from 'lucide-react';
import { SearchClinicsByAddress, Clinic } from '../database';
import ClinicsMap from '../components/ClinicsMap';
import { GOOGLE_MAPS_API_KEY } from '../utils/googleMapsApiKey';

const SearchClinics = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [searchLocation, setSearchLocation] = useState<{ lat: number, lng: number } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [locationPermission, setLocationPermission] = useState<'prompt' | 'granted' | 'denied'>('prompt');
    const [autoFilledFromStorage, setAutoFilledFromStorage] = useState(false);

    // Function to get address from coordinates
    const getAddressFromCoordinates = async (latitude: number, longitude: number): Promise<string> => {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();

            console.log('Geocoding response:', data); // Dodaj to dla debugowania

            if (data.status === 'OK' && data.results.length > 0) {
                return data.results[0].formatted_address;
            } else if (data.status === 'ZERO_RESULTS') {
                throw new Error('No address found for these coordinates');
            } else if (data.status === 'REQUEST_DENIED') {
                throw new Error('Google Maps API key is invalid or missing required APIs');
            } else {
                throw new Error(`Geocoding failed: ${data.status}`);
            }
        } catch (error) {
            console.error('Error getting address:', error);
            throw error;
        }
    };

    // Function to search clinics
    const searchClinics = async (address: string) => {
        if (!address.trim()) return;

        console.log('Searching for clinics at address:', address);
        setIsLoading(true);
        setError(null);
        setHasSearched(true);
        setSearchTerm(address);
        
        try {
            const result = await SearchClinicsByAddress(address);
            setClinics(result.clinics);
            setSearchLocation(result.searchLocation);
        } catch (err) {
            setError(t('searchClinics.error'));
            console.error('Error searching clinics:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form submission
    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        await searchClinics(searchTerm);
    };

    // Get user's location on initial load
    useEffect(() => {
        const getUserLocation = async () => {
            if (!navigator.geolocation) {
                console.log('Geolocation is not supported by your browser');
                // Sprawdź localStorage jeśli geolokalizacja nie jest dostępna
                checkLocalStorageForLocation();
                return;
            }

            try {
                const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    });
                });

                setLocationPermission('granted');
                const address = await getAddressFromCoordinates(
                    position.coords.latitude,
                    position.coords.longitude
                );
                await searchClinics(address);
            } catch (error) {
                console.log('Error getting location:', error);
                if (error instanceof GeolocationPositionError) {
                    if (error.code === error.PERMISSION_DENIED) {
                        setLocationPermission('denied');
                        // Sprawdź localStorage jeśli użytkownik odmówił lokalizacji
                        checkLocalStorageForLocation();
                    }
                } else {
                    // Sprawdź localStorage jeśli wystąpił inny błąd
                    checkLocalStorageForLocation();
                }
            }
        };

        // Sprawdź localStorage dla zapisanej lokalizacji
        const checkLocalStorageForLocation = () => {
            const saved = localStorage.getItem('userLocation');
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    if (parsed.postalCode) {
                        console.log('Found saved location in localStorage:', parsed);
                        // Użyj kodu pocztowego zamiast nazwy kraju
                        setSearchTerm(parsed.postalCode);
                        setAutoFilledFromStorage(true);
                        // Automatycznie wyszukaj kliniki dla zapisanej lokalizacji
                        searchClinics(parsed.postalCode);
                    }
                } catch (error) {
                    console.error('Error parsing saved location:', error);
                }
            }
        };

        // Only try to get location if we haven't searched yet
        if (!hasSearched) {
            getUserLocation();
        }
    }, []);

    return (
        <>
            <Helmet>
                <title>{t('searchClinics.title')} | SafeTripVax</title>
                <meta name="description" content={t('searchClinics.metaDescription')} />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
                <div className="container mx-auto px-4 py-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">
                                {t('searchClinics.title')}
                            </h1>
                            <p className="text-lg text-gray-600">
                                {t('searchClinics.subtitle')}
                            </p>
                        </div>

                        <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
                            <div className="flex gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={t('searchClinics.searchPlaceholder')}
                                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm transition-all duration-300"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isLoading || !searchTerm.trim()}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isLoading ? t('searchClinics.searching') : t('searchClinics.search')}
                                </button>
                            </div>
                            
                            {/* Informacja o automatycznym wypełnieniu */}
                            {autoFilledFromStorage && (
                                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
                                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm">
                                        {t('searchClinics.savedLocation.autoFilled')}
                                    </p>
                                </div>
                            )}
                            
                            {locationPermission === 'denied' && (
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center text-yellow-700">
                                    <MapPin className="w-5 h-5 mr-2 flex-shrink-0" />
                                    <p className="text-sm">
                                        {t('searchClinics.locationDenied')}
                                    </p>
                                </div>
                            )}
                        </form>

                        {error && (
                            <div className="text-red-600 mb-4">
                                {error}
                            </div>
                        )}

                        {isLoading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
                                <p className="text-gray-600">{t('searchClinics.searching')}</p>
                            </div>
                        ) : hasSearched && (
                            <>
                                {/* Zawsze pokazuj mapę z lokalizacją użytkownika */}
                                {searchLocation && (
                                    <div className="space-y-8">
                                        <ClinicsMap clinics={clinics} searchLocation={searchLocation} className="h-[500px]" />
                                        
                                        {clinics.length > 0 ? (
                                            <>
                                                {/* Komunikat o pokazywaniu najbliższych klinik w kraju */}
                                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
                                                    <div className="flex items-center">
                                                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        <p className="text-sm font-medium">
                                                            {t('searchClinics.searchResults.showingNearestInCountry')}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-4">
                                                    {clinics.map((clinic, index) => (
                                                        <div 
                                                            key={clinic.id} 
                                                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                                                        >
                                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                                                <div className="space-y-2 flex-1">
                                                                    <div className="flex items-center gap-3">
                                                                        <h2 className="text-xl font-semibold text-gray-800">{clinic.name}</h2>
                                                                        {index === 0 && (
                                                                            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                                                                Najbliższa
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="space-y-1 text-gray-600">
                                                                        <p className="flex items-center">
                                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                            </svg>
                                                                            {clinic.address}
                                                                        </p>
                                                                        <p className="flex items-center">
                                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                                            </svg>
                                                                            {clinic.phone_number}
                                                                        </p>
                                                                        <p className="flex items-center">
                                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                                            </svg>
                                                                            {clinic.postal_code} {clinic.city}
                                                                        </p>
                                                                        {clinic.distance && (
                                                                            <p className="flex items-center text-blue-600 font-medium">
                                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                                                </svg>
                                                                                Odległość: {clinic.distance.toFixed(1)} km
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex-shrink-0">
                                                                    <a
                                                                        href={clinic.website_url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
                                                                    >
                                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                        </svg>
                                                                        {t('searchClinics.visitWebsite')}
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-center py-8">
                                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                                                    <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                                                    </svg>
                                                    <p className="text-gray-600">{t('searchClinics.noResults')}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchClinics; 